import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Typography,
  Badge,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import CommentIcon from '@mui/icons-material/Comment';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BrushIcon from '@mui/icons-material/Brush';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import ExportMenu from './ExportMenu';

const EditModeToolbar = ({
  mode,
  onModeChange,
  projectName,
  onBack,
  onZoomIn,
  onZoomOut,
  onFit,
  unresolvedCommentCount = 0,
  projectId,
  onSettingsOpen,
  framePicker,
}) => {
  const MODES = [
    { key: 'chat', icon: <ChatIcon sx={{ fontSize: 16 }} />, label: 'Chat' },
    { key: 'edit', icon: <EditIcon sx={{ fontSize: 16 }} />, label: 'Edit' },
    {
      key: 'comment',
      label: 'Comments',
      icon: (
        <Badge
          badgeContent={unresolvedCommentCount || null}
          sx={{ '& .MuiBadge-badge': { fontSize: 8, minWidth: 14, height: 14, background: '#e85d26', color: '#fff' } }}
        >
          <CommentIcon sx={{ fontSize: 16 }} />
        </Badge>
      ),
    },
    { key: 'files', icon: <CodeIcon sx={{ fontSize: 16 }} />, label: 'Files' },
    { key: 'draw', icon: <BrushIcon sx={{ fontSize: 16 }} />, label: 'Draw' },
    { key: 'tweaks', icon: <AutoFixHighIcon sx={{ fontSize: 16 }} />, label: 'AI Tweaks' },
    { key: 'present', icon: <PlayArrowIcon sx={{ fontSize: 16 }} />, label: 'Present' },
  ];
  return (
    <Box
      sx={{
        height: 48,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1,
        borderBottom: '1px solid #eee',
        background: '#fff',
        flexShrink: 0,
      }}
    >
      <Tooltip title="Back to projects">
        <IconButton size="small" onClick={onBack} sx={{ color: '#666' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', mr: 1 }}>
        {projectName || 'Design Studio'}
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Mode buttons */}
      <Box
        sx={{
          display: 'flex',
          background: '#f5f5f5',
          borderRadius: 1.5,
          p: 0.25,
          gap: 0.25,
        }}
      >
        {MODES.map(({ key, icon, label }) => (
          <Tooltip key={key} title={label}>
            <IconButton
              size="small"
              onClick={() => onModeChange(key)}
              sx={{
                borderRadius: 1,
                color: mode === key ? '#fff' : '#888',
                background: mode === key ? '#1a1a1a' : 'transparent',
                '&:hover': {
                  background: mode === key ? '#333' : 'rgba(0,0,0,0.06)',
                },
                width: 30,
                height: 28,
              }}
            >
              {icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Frame picker (injected from parent) */}
      {framePicker && <Box sx={{ mr: 0.5 }}>{framePicker}</Box>}

      {/* Share */}
      <Tooltip title="Copy link">
        <IconButton
          size="small"
          onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); }}
          sx={{ color: '#666' }}
        >
          <ShareIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Export */}
      {projectId && <ExportMenu projectId={projectId} />}

      {/* Settings */}
      {onSettingsOpen && (
        <Tooltip title="Project settings">
          <IconButton size="small" onClick={onSettingsOpen} sx={{ color: '#666' }}>
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Zoom controls */}
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={onZoomOut} sx={{ color: '#666' }}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to screen">
          <IconButton size="small" onClick={onFit} sx={{ color: '#666' }}>
            <FitScreenIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={onZoomIn} sx={{ color: '#666' }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default EditModeToolbar;
